/**
 * R6 — PdfTableImporter.jsx
 *
 * Modal che mostra le tabelle estratte da un PDF caricato, lascia
 * all'operatore scegliere quale importare e con quale famiglia di default,
 * e inserisce le righe come voci scheletro nella griglia.
 */
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Loader2,
  Table as TableIcon,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatErrorDetail } from "../lib/api";

const FAMIGLIE = [
  ["F01", "F01 Tubi"],
  ["F02", "F02 Raccordi"],
  ["F03", "F03 Raccordi saldati"],
  ["F04", "F04 Flange"],
  ["F05", "F05 Valvole"],
  ["F06", "F06 Valvole regolazione"],
  ["F08", "F08 Strumentazione"],
  ["F10", "F10 Pompe"],
  ["F11", "F11 Scambiatori"],
  ["F12", "F12 Accessori"],
];

export default function PdfTableImporter({
  open,
  onClose,
  tables = [],
  offerId,
  onImported,
}) {
  const [selected, setSelected] = useState(0);
  const [famiglia, setFamiglia] = useState("F05");
  const [importing, setImporting] = useState(false);

  if (!tables.length) return null;

  const t = tables[selected] || tables[0];

  const doImport = async () => {
    if (!offerId || !t) return;
    setImporting(true);
    try {
      const res = await api.pdfTablesImport(offerId, t.rows, famiglia);
      toast.success(`${res.imported} voci importate dal PDF`);
      onImported?.(res);
      onClose?.();
    } catch (e) {
      toast.error(formatErrorDetail(e));
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !importing && onClose?.()}>
      <DialogContent
        className="max-w-[720px] max-h-[82vh] flex flex-col"
        data-testid="pdf-table-importer"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TableIcon
              className="h-5 w-5"
              style={{ color: "var(--accent-primary)" }}
            />
            {tables.length === 1
              ? "Tabella rilevata nel PDF"
              : `${tables.length} tabelle rilevate nel PDF`}
          </DialogTitle>
          <DialogDescription>
            Rivedi l'anteprima, scegli la famiglia di default e importa le
            righe come voci nella griglia.
          </DialogDescription>
        </DialogHeader>

        {/* Tabs — only if multiple tables */}
        {tables.length > 1 && (
          <div
            className="flex items-center gap-1 border-b pb-2"
            style={{ borderColor: "var(--border-subtle)" }}
            data-testid="table-tabs"
          >
            {tables.map((tt, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setSelected(i)}
                className="h-7 px-2 rounded text-[11px] font-medium transition-colors"
                style={{
                  background:
                    selected === i ? "var(--accent-subtle)" : "transparent",
                  color:
                    selected === i
                      ? "var(--accent-primary)"
                      : "var(--text-secondary)",
                }}
                data-testid={`table-tab-${i}`}
              >
                Tab {i + 1} · {tt.rows_total} righe
              </button>
            ))}
          </div>
        )}

        {/* Preview table */}
        <div className="flex-1 overflow-auto border rounded"
             style={{ borderColor: "var(--border-subtle)" }}>
          <table className="w-full text-[11px]"
                 data-testid={`preview-table-${selected}`}>
            <thead>
              <tr
                style={{
                  background: "var(--bg-subtle)",
                  borderBottom: "1px solid var(--border-subtle)",
                }}
              >
                {Object.entries(t.header_mapping || {}).map(([idx, key]) => (
                  <th
                    key={idx}
                    className="text-left px-2 py-1.5 text-[10px] uppercase tracking-wide font-semibold"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(t.rows || []).slice(0, 20).map((r, i) => (
                <tr
                  key={i}
                  style={{ borderBottom: "1px solid var(--border-subtle)" }}
                >
                  {Object.entries(t.header_mapping || {}).map(([idx, key]) => (
                    <td
                      key={idx}
                      className="px-2 py-1"
                      style={{ color: "var(--text-primary)" }}
                    >
                      {r[key] || "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {t.rows_total > 20 && (
            <div
              className="px-2 py-1.5 text-[10px] text-center"
              style={{
                background: "var(--bg-subtle)",
                color: "var(--text-muted)",
              }}
            >
              +{t.rows_total - 20} righe non mostrate nell'anteprima
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-between gap-2 pt-2 text-[11px]"
          style={{ color: "var(--text-muted)" }}
        >
          <div>
            Confidenza mapping: <strong>{t.confidence_pct}%</strong>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1">
              Famiglia:
              <select
                value={famiglia}
                onChange={(e) => setFamiglia(e.target.value)}
                className="h-7 px-1.5 rounded text-[11px]"
                style={{ border: "1px solid var(--border-subtle)" }}
                data-testid="import-famiglia"
              >
                {FAMIGLIE.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={onClose}
              disabled={importing}
              className="h-7 px-3 rounded text-[11px]"
              style={{
                border: "1px solid var(--border-subtle)",
                color: "var(--text-primary)",
              }}
              data-testid="import-cancel"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={doImport}
              disabled={importing || (t.rows || []).length === 0}
              className="inline-flex items-center gap-1 h-7 px-3 rounded text-[11px] font-medium text-white disabled:opacity-40"
              style={{ background: "var(--accent-primary)" }}
              data-testid="import-confirm"
            >
              {importing ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Download className="h-3 w-3" />
              )}
              Importa {t.rows_total} voci
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
